import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export interface Notification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  duration?: number // in milliseconds, 0 = permanent
  actions?: {
    label: string
    action: () => void
  }[]
}

export const useNotificationStore = defineStore('notifications', () => {
  // State
  const notifications = ref<Notification[]>([])
  const timers = new Map<string, NodeJS.Timeout>()

  // Computed
  const currentNotification = computed(() => notifications.value[0] || null)
  const hasNotifications = computed(() => notifications.value.length > 0)

  // Actions
  function show(notification: Omit<Notification, 'id'>) {
    const id = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const duration = notification.duration ?? 5000 // Default 5 seconds

    const newNotification: Notification = {
      id,
      ...notification
    }

    notifications.value.unshift(newNotification)

    // Auto-remove after duration (if not permanent)
    if (duration > 0) {
      const timer = setTimeout(() => {
        remove(id)
      }, duration)
      
      timers.set(id, timer)
    }

    return id
  }

  function remove(id: string) {
    const index = notifications.value.findIndex(n => n.id === id)
    if (index > -1) {
      notifications.value.splice(index, 1)
      
      // Clear timer if exists
      const timer = timers.get(id)
      if (timer) {
        clearTimeout(timer)
        timers.delete(id)
      }
    }
  }

  function clear() {
    // Clear all timers
    timers.forEach(timer => clearTimeout(timer))
    timers.clear()
    
    // Clear notifications
    notifications.value = []
  }

  function success(title: string, message?: string, duration?: number) {
    return show({ type: 'success', title, message, duration })
  }

  function error(title: string, message?: string, duration?: number) {
    return show({ type: 'error', title, message, duration })
  }

  function warning(title: string, message?: string, duration?: number) {
    return show({ type: 'warning', title, message, duration })
  }

  function info(title: string, message?: string, duration?: number) {
    return show({ type: 'info', title, message, duration })
  }

  // Spark-specific notifications
  function sparkConsumed(amount: number, newBalance: number) {
    return success(
      'Sparks Consumed',
      `Used ${amount} sparks. New balance: ${newBalance.toLocaleString()}`
    )
  }

  function sparkAdded(amount: number, newBalance: number) {
    return success(
      'Sparks Added',
      `Added ${amount} sparks. New balance: ${newBalance.toLocaleString()}`
    )
  }

  function insufficientSparks(required: number, available: number) {
    return error(
      'Insufficient Sparks',
      `Need ${required} sparks but only have ${available}. Buy more sparks to continue.`,
      0 // Permanent until dismissed
    )
  }

  function subscriptionChanged(action: 'canceled' | 'reactivated' | 'created') {
    const messages = {
      canceled: 'Your subscription will be canceled at the end of the current billing period.',
      reactivated: 'Your subscription has been reactivated successfully.',
      created: 'Welcome! Your subscription is now active.'
    }

    return success(
      'Subscription Updated',
      messages[action]
    )
  }

  return {
    // State
    notifications,

    // Computed
    currentNotification,
    hasNotifications,

    // General actions
    show,
    remove,
    clear,
    success,
    error,
    warning,
    info,

    // Spark-specific actions
    sparkConsumed,
    sparkAdded,
    insufficientSparks,
    subscriptionChanged
  }
})